/**
 * Statut Connect affiché côté backoffice, évite de traiter « inconnu » comme « configuré ».
 */

export type StripeConnectUiStatus = 'none' | 'unknown' | 'pending' | 'active';

export type SettingsStripeBadge =
  | 'not_configured'
  | 'checking'
  | 'pending'
  | 'configured';

export function normalizeStripeConnectStatus(
  accountId: string | null | undefined,
  status: string | null | undefined
): StripeConnectUiStatus {
  if (!accountId?.trim()) return 'none';
  if (status === 'active') return 'active';
  if (status === 'pending') return 'pending';
  return 'unknown';
}

/** True uniquement si le compte est confirmé actif (pas unknown / pending). */
export function isStripeConnectReady(
  accountId: string | null | undefined,
  status: string | null | undefined
): boolean {
  return normalizeStripeConnectStatus(accountId, status) === 'active';
}

/**
 * Paiement en ligne (Services / forfaits) : uniquement Connect `active`.
 * `null` (check en cours) ou `false` (mid-KYC / absent) → bloqué.
 * Aligné ForfaitsPage (`status === "active"`), plus strict que « acct_* présent ».
 */
export function canEnableOnlinePayment(
  connectReady: boolean | null | undefined
): boolean {
  return connectReady === true;
}

/**
 * Badge Settings : `liveStatus === null` (check en cours / échec) ≠ configuré.
 * Avec un account_id, seul `active` affiche le badge vert.
 */
export function resolveSettingsStripeBadge(
  accountId: string | null | undefined,
  liveStatus: string | null | undefined
): SettingsStripeBadge {
  if (!accountId?.trim()) return 'not_configured';
  if (liveStatus == null) return 'checking';
  if (liveStatus === 'active') return 'configured';
  if (liveStatus === 'pending') return 'pending';
  return 'pending';
}

function unwrapProPayload(data: unknown): Record<string, unknown> | null {
  if (!data || typeof data !== 'object') return null;
  const rec = data as Record<string, unknown>;
  const nested = rec.pro;
  if (nested && typeof nested === 'object') {
    return nested as Record<string, unknown>;
  }
  return rec;
}

/**
 * Auth /get-profile n’expose pas Connect : copier `stripe_account_id` depuis GET /get-pro.
 * Payload plat (API) ou `{ pro }` (Settings unwrap). Whitespace → absent.
 */
export function applyProStripeAccountIdToUser<T extends { stripe_account_id?: string }>(
  user: T,
  proPayload: unknown
): T {
  const pro = unwrapProPayload(proPayload);
  if (!pro) return user;
  const raw = pro.stripe_account_id;
  const id = typeof raw === 'string' ? raw.trim() : '';
  if (!id) {
    if (!user.stripe_account_id) return user;
    const next = { ...user };
    delete next.stripe_account_id;
    return next;
  }
  if (user.stripe_account_id === id) return user;
  return { ...user, stripe_account_id: id };
}
